// @givmo/auth — PKCE (RFC 7636) for the employer SSO ceremonies: the portal's test connection (AS-WEB6)
// and the consoles' organization sign-in (AS-WEB30). Moved here from `@givmo/employer-module`
// (`features/security/sso-pkce`, which re-exports it) when the sign-in door needed it too: one
// implementation of the binding for every ceremony.
//
// ══ THE ONE BINDING, SPENT OR NOT ════════════════════════════════════════════════════════════════
// Both ceremonies are the REAL SP-initiated flow, and the backend takes the same `code_challenge` for
// each. Where the verifier goes differs, and that is a fact about the ceremony, not about PKCE:
//   - The organization SIGN-IN spends it: the page that started the ceremony presents it with the
//     one-use handoff code at `POST /authn/sso/token` (`organization-sso.ts`), so a handoff code stolen
//     on its way back buys nothing without it.
//   - The TEST CONNECTION never spends it: a finished test carries no handoff code back (the callback
//     redirects `?sso_test=complete` and nothing else), and the outcome is read from the Owner-gated
//     `GET …/test-connection/{test_id}`. A test is chosen deliberately over the broker's own test mode,
//     so it exercises the path employees take; weakening its binding "because it is only a test"
//     would test a different flow. Its verifier is generated, hashed, and dropped — kept for the life
//     of the attempt only because {@link createPkcePair} returns a pair.
//
// ══ NO NEW DEPENDENCY ═════════════════════════════════════════════════════════════════════════
// SHA-256 comes from Web Crypto (`crypto.subtle.digest`), which every browser the consoles support
// has had for a decade, and the base64url encoder is this package's own — the one the passkey codec
// uses (`webauthn.ts`), so there is exactly one implementation of that alphabet in the web estate.

import { base64UrlEncode } from './webauthn';

/** RFC 7636 §4.1 bounds, matching the backend's `PKCE_VERIFIER_{MIN,MAX}_LENGTH` schema pins. */
export const PKCE_VERIFIER_MIN_LENGTH = 43;
export const PKCE_VERIFIER_MAX_LENGTH = 128;

/** The only method this product speaks. `plain` binds nothing and the backend does not accept it. */
export const PKCE_METHOD_S256 = 'S256';

export interface PkcePair {
  /** The high-entropy secret. Sent only by a sign-in, with its handoff code — see the header. */
  verifier: string;
  /** `base64url(sha256(verifier))`, unpadded — what `/authn/sso/route` and `…/test-connection` carry. */
  challenge: string;
}

/**
 * The crypto this module needs, as a seam.
 *
 * Production passes nothing and gets `globalThis.crypto`. It exists so a test can drive a KNOWN
 * verifier through the hash and compare against an independently-computed vector, which is the only
 * way to prove the encoding is right — a self-consistent round trip through our own encoder would
 * pass just as happily with a wrong alphabet.
 */
export interface PkceCrypto {
  getRandomValues<T extends ArrayBufferView>(array: T): T;
  subtle: Pick<SubtleCrypto, 'digest'>;
}

/**
 * 32 bytes of entropy as unpadded base64url — 43 characters, exactly the RFC minimum and every
 * character legal in the unreserved set.
 *
 * 32 bytes rather than the maximum the field allows: the binding's strength is the entropy, not the
 * string length, and 256 bits is what the rest of this product's opaque tokens carry.
 */
export function createCodeVerifier(crypto: PkceCrypto = defaultCrypto()): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return base64UrlEncode(bytes);
}

/**
 * `base64url(sha256(verifier))`, unpadded (RFC 7636 §4.2 `S256`).
 *
 * ⚠ THE DIGEST IS WRAPPED IN A `Uint8Array` BEFORE ENCODING, AND THAT IS NOT DECORATION.
 * `base64UrlEncode` branches on `input instanceof ArrayBuffer`, and `instanceof` is REALM-SCOPED —
 * a buffer minted by a `SubtleCrypto` from another realm (Node's webcrypto under jsdom, an iframe,
 * a worker) fails that check, falls into the ArrayBufferView branch, and reads `.buffer` /
 * `.byteOffset` off an object that has neither. The result is not an exception: it is an EMPTY
 * STRING, i.e. a silently unbound ceremony that the backend rejects as a 422 with nothing on the
 * client saying why. A `Uint8Array` view is unambiguous in every realm. Caught by this module's own
 * suite, which is the only place the cross-realm case occurs today — and precisely why the RFC
 * vector is asserted rather than a round trip, which would have been empty-equals-empty and green.
 */
export async function createCodeChallenge(
  verifier: string,
  crypto: PkceCrypto = defaultCrypto(),
): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

/** A fresh verifier + its challenge. One call per ceremony; never reused across attempts. */
export async function createPkcePair(crypto: PkceCrypto = defaultCrypto()): Promise<PkcePair> {
  const verifier = createCodeVerifier(crypto);
  return { verifier, challenge: await createCodeChallenge(verifier, crypto) };
}

/**
 * Whether a verifier is spellable on the wire (RFC 7636's unreserved set, within the bounds).
 *
 * Not used to guard our own output — {@link createCodeVerifier} cannot produce anything else — but
 * exported so the suite can assert that property over many draws rather than trusting the encoder by
 * inspection.
 */
export function isValidCodeVerifier(value: string): boolean {
  return (
    value.length >= PKCE_VERIFIER_MIN_LENGTH &&
    value.length <= PKCE_VERIFIER_MAX_LENGTH &&
    /^[A-Za-z0-9\-._~]+$/.test(value)
  );
}

function defaultCrypto(): PkceCrypto {
  const source = globalThis.crypto;
  // A browser without Web Crypto cannot do this ceremony at all. Throwing names the reason at the
  // point of failure instead of sending an empty challenge the backend would refuse as a 422.
  if (!source?.subtle) throw new Error('Web Crypto is unavailable in this browser.');
  return source as unknown as PkceCrypto;
}
